/**
 * AI Integration Panel Component
 * Allows configuration of AI integration mode (Integrated vs Standalone)
 * and external PMS URL when needed
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Link2, AlertCircle, CheckCircle2, Server } from 'lucide-react';
import { BACKEND_URL, API } from '@/lib/backendUrl';

export function AiIntegrationPanel({ 
  config, 
  onUpdateConfig, 
  aiAgentsEnabled 
}) {
  if (!aiAgentsEnabled) {
    return null;
  }

  const handleModeChange = (mode) => {
    onUpdateConfig({
      ...config,
      ai_integration_mode: mode,
      // Clear external URL if switching back to integrated
      external_pms_url: mode === 'integrated' ? null : config.external_pms_url
    });
  };

  const handleUrlChange = (e) => {
    onUpdateConfig({
      ...config,
      external_pms_url: e.target.value || null
    });
  };

  const isStandalone = config?.ai_integration_mode === 'standalone';
  const hasExternalUrl = config?.external_pms_url && config.external_pms_url.trim().length > 0;
  const isIntegrated = config?.ai_integration_mode === 'integrated';

  return (
    <Card className="border-primary/20" data-testid="ai-integration-panel">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Link2 className="w-5 h-5 text-primary" strokeWidth={1.75} />
          </div>
          <div>
            <CardTitle className="text-lg heading-font">AI Integration Mode</CardTitle>
            <CardDescription className="text-xs">
              Configure how AI agents connect to your hotel data
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Integration Mode Selector */}
        <div className="space-y-2">
          <Label htmlFor="integration-mode" className="text-sm font-medium">
            Integration Type
          </Label>
          <Select 
            value={config?.ai_integration_mode || 'integrated'} 
            onValueChange={handleModeChange}
          >
            <SelectTrigger id="integration-mode" data-testid="integration-mode-select">
              <SelectValue placeholder="Select integration mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="integrated" data-testid="mode-integrated">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  <div>
                    <p className="font-medium">Integrated</p>
                    <p className="text-xs text-muted-foreground">Use getbitsy.ai PMS</p>
                  </div>
                </div>
              </SelectItem>
              <SelectItem value="standalone" data-testid="mode-standalone">
                <div className="flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  <div>
                    <p className="font-medium">Standalone</p>
                    <p className="text-xs text-muted-foreground">Connect to external PMS</p>
                  </div>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Integrated Mode Info */}
        {isIntegrated && config?.pms_enabled && (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-xs text-green-800">
              AI agents will use data from your getbitsy.ai PMS (bookings, rooms, staff, guests).
            </AlertDescription>
          </Alert>
        )}

        {/* Standalone Mode - External PMS URL */}
        {isStandalone && (
          <div className="space-y-3">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                <strong>Standalone mode:</strong> AI agents will fetch data from your external PMS API.
                You can disable the built-in getbitsy.ai PMS and use AI features independently.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="external-pms-url" className="text-sm font-medium">
                External PMS API URL <span className="text-destructive">*</span>
              </Label>
              <Input
                id="external-pms-url"
                type="url"
                placeholder="https://your-pms-api.example.com/api/v1"
                value={config?.external_pms_url || ''}
                onChange={handleUrlChange}
                className="mono text-sm"
                data-testid="external-pms-url-input"
              />
              <p className="text-xs text-muted-foreground">
                Provide the base URL for your PMS API. AI agents will authenticate and fetch data using this endpoint.
              </p>
              
              {!hasExternalUrl && (
                <Alert variant="destructive" className="mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    External PMS URL is required when using standalone mode.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        )}

        {/* Warning: Integrated mode but PMS disabled */}
        {isIntegrated && !config?.pms_enabled && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <strong>Configuration issue:</strong> Integrated mode requires the getbitsy.ai PMS to be enabled.
              Either enable PMS or switch to Standalone mode with an external PMS URL.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
